import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import { ErrorPanel } from '../../../../../../common/widgets/ErrorPanel';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { DropdownOptionModel } from '../../../../../benefit/models/Common';
import { ErrorState, ErrorType } from '../../../../../benefit/models/Errors';
import { resources } from '../../../../resources/resources';
import { merchantAccountsService } from '../../../../services/merchant-accounts';

interface EditAccountDialogProps {
  accountId: number;
  closeDialog: () => void;
  onUserEdited: (accountId: number, form: EditUserForm) => void;
}

export interface EditUserForm {
  role?: DropdownOptionModel;
  firstname?: string;
  lastname?: string;
  phoneNumber?: string;
}

export const EditAccountDialog = (props: EditAccountDialogProps) => {
  const { accountId, closeDialog, onUserEdited } = props;
  const [form, setForm] = useState<EditUserForm>({});
  const [errors, setErrors] = useState<ErrorState>({});
  const [roles, setRoles] = useState<DropdownOptionModel[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const { t: translate } = useTranslation();

  const loadRoles = async (abortController?: AbortController) => {
    setIsLoading(true);
    const resp = await merchantAccountsService.getAccountRoles({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) return;
    setIsLoading(false);

    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      setRoles(resp.data);
    }
  };

  const loadUser = async (abortController?: AbortController) => {
    setIsLoading(true);
    const resp = await merchantAccountsService.getEditAccountReadModel(
      accountId,
      {
        signal: abortController?.signal,
      },
    );
    if (abortController?.signal.aborted) return;
    setIsLoading(false);

    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      setForm({
        firstname: resp.data.firstname,
        lastname: resp.data.lastname,
        phoneNumber: resp.data.phoneNumber,
        role: resp.data.role,
      });
    }
  };

  useMount(abortController => {
    loadRoles(abortController);
    loadUser(abortController);
  });

  const clearErrors = (field: string) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const onSaveClick = async () => {
    setIsSaveLoading(true);
    const resp = await merchantAccountsService.updateAccount({
      accountId,
      roleId: form.role?.value,
      firstname: form.firstname,
      lastname: form.lastname,
      phoneNumber: form.phoneNumber,
    });
    setIsSaveLoading(false);
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      showSuccessToast(translate(resources.sveChangesConfirmation));
      closeDialog();
      onUserEdited(accountId, form);
    }
  };

  if (isLoading)
    return (
      <Dialog closeDialog={closeDialog} showCloseIcon>
        <LoadingSpinner />
      </Dialog>
    );

  if (showErrorPanel)
    return (
      <Dialog closeDialog={closeDialog} showCloseIcon>
        <ErrorPanel />
      </Dialog>
    );

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      className="dialog-merchants-edit"
    >
      <Loader loading={isLoading} loader={<LoadingSpinner />}>
        <div className="dialog-content">
          <div className="dialog-title">
            {translate(resources.accounts.dialog.edit.title)}
          </div>
          <div>
            <Input
              label={translate(resources.accounts.dialog.firstName)}
              value={form.firstname}
              onChange={e =>
                setForm(
                  produce(d => {
                    d.firstname = e.value;
                  }),
                )
              }
              errors={errors.firstname}
              clearErrors={() => clearErrors('firstname')}
              mandatory
            />
            <Input
              label={translate(resources.accounts.dialog.lastName)}
              value={form.lastname}
              onChange={e =>
                setForm(
                  produce(d => {
                    d.lastname = e.value;
                  }),
                )
              }
              errors={errors.lastname}
              clearErrors={() => clearErrors('lastname')}
              mandatory
            />
            <Input
              label={translate(resources.accounts.dialog.phoneNumber)}
              value={form.phoneNumber}
              onChange={e =>
                setForm(
                  produce(d => {
                    d.phoneNumber = e.value;
                  }),
                )
              }
              errors={errors.phoneNumber}
              clearErrors={() => clearErrors('phoneNumber')}
              mandatory
            />
            <Dropdown
              label={translate(resources.accounts.dialog.role.label)}
              value={
                form.role ?? { text: translate(resources.select), value: null }
              }
              onChange={e =>
                setForm(
                  produce(d => {
                    d.role = e.value;
                  }),
                )
              }
              data={roles ?? []}
              textField="text"
              dataItemKey="value"
              errors={errors.role}
              clearErrors={() => clearErrors('role')}
              helper={translate(resources.accounts.dialog.role.info)}
              mandatory
              controlClassName="mb-0"
            />
          </div>
        </div>
        <div className="dialog-actions vertical">
          <Button
            className="btn btn-solid"
            onClick={onSaveClick}
            loading={isSaveLoading}
          >
            <span className="btn-text">{translate(resources.saveChanges)}</span>
          </Button>
        </div>
      </Loader>
    </Dialog>
  );
};
